
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { format, parse } from 'date-fns';
import getSeoDescription from '@/components/partials/war-room/getSeoDescription';
import WarRoomPost from '@/components/partials/war-room/WarRoomPost.vue';
import WarRoomRules from '@/components/partials/war-room/WarRoomRules.vue';
import WarRoomGrid from '@/components/partials/war-room/WarRoomGrid/WarRoomGrid.vue';
import WarRoomPostBreadcrumbs from '@/components/partials/war-room/WarRoomPostBreadcrumbs/WarRoomPostBreadcrumbs.vue';
import WarRoomRelatedPosts from '@/components/partials/war-room/WarRoomRelatedPosts.vue';
import WarRoomShareModal from '@/components/partials/war-room/WarRoomShare/WarRoomShareModal.vue';
import WarRoomConfirmRemovePost from '@/components/partials/war-room/WarRoomConfirmRemovePost.vue';
import GateWidget from '@/components/partials/war-room/GateWidget/GateWidget.vue';
import WarRoomSignUpWidget from '~/components/partials/war-room/WarRoomSignUp/WarRoomSignUpWidget.vue';
import { makeUrl } from '~/components/utils/browser/url';
import { POSTS_PAGE } from '~/constants';
import type { ITopic } from '~/types/common';

const POST_PAGE_PATH_PREFIX = POSTS_PAGE.path + '/';

export default Vue.extend({
  name: 'WarRoomPostShowPage',
  components: {
    WarRoomSignUpWidget,
    WarRoomRules,
    WarRoomPost,
    WarRoomPostBreadcrumbs,
    WarRoomGrid,
    WarRoomRelatedPosts,
    WarRoomShareModal,
    WarRoomConfirmRemovePost,
    GateWidget,
  },
  middleware({ route, query, redirect }) {
    // to support old links
    if (query.comment_id) {
      redirect(301, `${POST_PAGE_PATH_PREFIX}${route.params.postSlug}/comments/${query.comment_id}`);
    }
  },
  async fetch({ store, $auth, route, app }) {
    try {
      await Promise.all(
        [
          store.dispatch('warRoom/fetchPost', { postSlug: route.params.postSlug }),
          store.dispatch('warRoom/fetchRelatedPosts', { postSlug: route.params.postSlug }),
          // We need that data for proper title
          store.dispatch('warRoom/fetchTopics'),
          store.dispatch('warRoom/fetchPostComments', {
            query: route.query,
            postSlug: route.params.postSlug,
            page: 1,
            commentsMutation: 'setCommentTree',
          }),
          $auth.loggedIn && store.dispatch('warRoom/fetchSidebar'),
        ].filter(Boolean),
      );
    } catch (e) {
      app.$utils.nuxtErrorPage(e as Error);
      app.$sentryUtils.error(e);
    }
  },
  head() {
    const url = makeUrl(`${POST_PAGE_PATH_PREFIX}${this.$route.params.postSlug}`);
    const image = this.currentPost.og_image_url;
    const postTitle = this.currentPost.content?.data?.title;
    const category = this.currentPost.category.title;
    const topics = this.currentPost.topic_ids
      ?.map((postTopicId: number) => {
        return this.topics?.find(({ id }: ITopic) => postTopicId === id)?.name;
      })
      .join(', ');

    const dealDate = this.currentPost?.content?.data?.deal?.close_date
      ? format(parse(this.currentPost.content.data.deal.close_date, 'yyyy-mm-dd', new Date()), 'mm-dd-yyyy')
      : null;

    const title = [
      postTitle,
      ...(category ? [`[${category}]`] : []),
      ...(topics ? [`| ${topics}`] : []),
      ...(dealDate ? [`${dealDate}`] : []),
    ].join(' ');

    return this.$seo({
      title,
      description: getSeoDescription(this.$route.params.postSlug) || this.currentPost.seo_description,
      canonical: url,
      og: {
        url,
        ...(image && {
          image: {
            url: image,
          },
        }),
      },
      twitter: {
        card: 'summary_large_image',
      },
    });
  },
  computed: {
    ...mapGetters('warRoom', ['currentPost', 'topics']),
  },
  mounted() {
    this.$analytics.logEvent('war-room__post__page__load', { post_id: this.currentPost.id });
  },
});
